import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import { VisitorNameInput } from './VisitorNameInput';

export interface InputControlsProps {
  autoFocus?: boolean;
  buttonLabel?: string;
  buttonClassName?: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  inputLabel?: string;
  isRequired: boolean;
  onEnterIsDisabledChange?: (isDisabled: boolean) => void;
  onEnterWaitingRoom?: () => void;
  setVisitorName: (visitorName: string) => void;
  visitorName: string;
  /** @deprecated */
  showWaitingRoomButton?: boolean;
}

export function InputControls({
  autoFocus,
  buttonLabel,
  buttonClassName,
  children,
  className,
  disabled,
  inputLabel,
  isRequired,
  onEnterIsDisabledChange,
  onEnterWaitingRoom,
  setVisitorName: setVisitorNameProp,
  visitorName,
  showWaitingRoomButton = false,
}: InputControlsProps) {
  const t = useTranslate();
  const [visitorNameInputError, setVisitorNameInputError] = useState('');
  const isErrorBlockingSubmit =
    visitorNameInputError === t('telehealth-core:formErrorsCore.thisFieldIsRequiredPleaseEnter') ||
    (isRequired && visitorName.trim().length === 0);

  useEffect(
    function updateEnterIsDisabled() {
      onEnterIsDisabledChange?.(isErrorBlockingSubmit);
    },
    [isErrorBlockingSubmit, onEnterIsDisabledChange],
  );

  const setVisitorName = useCallback(
    (name: string, validationResult: true | string) => {
      setVisitorNameProp(name);
      setVisitorNameInputError(validationResult !== true ? validationResult : '');
    },
    [setVisitorNameProp],
  );

  // no <form> here, so button click and Enter press share a handler
  const handleEnterWaitingRoom = useCallback(() => {
    if (isErrorBlockingSubmit) return;
    onEnterWaitingRoom?.();
  }, [isErrorBlockingSubmit, onEnterWaitingRoom]);

  return (
    <div className={twMerge('flex flex-col gap-y-4', className)}>
      <VisitorNameInput
        autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
        className='w-full'
        disabled={disabled}
        isRequired={isRequired}
        label={inputLabel}
        setVisitorName={setVisitorName}
        visitorName={visitorName}
        onEnter={handleEnterWaitingRoom}
      />
      {showWaitingRoomButton && (
        <Button
          disabled={isErrorBlockingSubmit}
          onClick={handleEnterWaitingRoom}
          type='submit'
          use='secondary'
          className={twMerge('w-full', buttonClassName)}
        >
          {buttonLabel ?? t('telehealth-core:introRoom.enterWaitingRoom')}
        </Button>
      )}
      {children}
    </div>
  );
}
